/**
 * React Query hooks for suppression rules data fetching and mutations.
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  listSuppressionRules,
  createSuppressionRule,
  updateSuppressionRule,
  deleteSuppressionRule,
  previewSuppressionRule,
  type CreateSuppressionRuleData,
  type UpdateSuppressionRuleData,
  type SuppressionRulePreviewRequest,
  type SuppressionRuleCreateResponse,
} from "@/services/suppressionRulesService";
import { useApiReady } from "@/hooks/useApi";

/**
 * Hook to fetch a paginated list of suppression rules.
 * @param page - The page number (default: 1)
 * @param isActive - Optional filter by active status
 */
export function useSuppressionRules(page: number = 1, isActive?: boolean, itemsPerPage?: number) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["suppression-rules", page, isActive, itemsPerPage],
    queryFn: () => listSuppressionRules(page, isActive, itemsPerPage),
    staleTime: 30000, // 30 seconds
    enabled: isApiReady,
  });
}

export function useSuppressionRulePreview(criteria: SuppressionRulePreviewRequest, enabled: boolean) {
  const isApiReady = useApiReady();

  return useQuery({
    queryKey: ["suppression-rule-preview", criteria],
    queryFn: () => previewSuppressionRule(criteria),
    staleTime: 10000,
    enabled: isApiReady && enabled,
  });
}

/**
 * Hook to create a new suppression rule.
 * Invalidates the suppression rules list and activities list on settled.
 */
export function useCreateSuppressionRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSuppressionRuleData) => createSuppressionRule(data),
    onSettled: (data: SuppressionRuleCreateResponse | undefined, error) => {
      if (error) {
        toast.error("Failed to create suppression rule");
      } else if (data && data.suppressed_count > 0) {
        toast.success(`Suppression rule created — ${data.suppressed_count} existing issue(s) suppressed`);
      } else {
        toast.success("Suppression rule created");
      }
      queryClient.invalidateQueries({ queryKey: ["suppression-rules"] });
      queryClient.invalidateQueries({ queryKey: ["issue-activities"] });
      queryClient.invalidateQueries({ queryKey: ["security-issues"] });
    },
  });
}

/**
 * Hook to update a suppression rule.
 * Invalidates the suppression rules list on settled.
 */
export function useUpdateSuppressionRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ ruleId, data }: { ruleId: number; data: UpdateSuppressionRuleData }) =>
      updateSuppressionRule(ruleId, data),
    onSettled: (data: SuppressionRuleCreateResponse | undefined, error) => {
      if (error) {
        toast.error("Failed to update suppression rule");
      } else if (data && data.suppressed_count > 0) {
        toast.success(`Suppression rule updated — ${data.suppressed_count} existing issue(s) suppressed`);
      } else {
        toast.success("Suppression rule updated");
      }
      queryClient.invalidateQueries({ queryKey: ["suppression-rules"] });
      queryClient.invalidateQueries({ queryKey: ["security-issues"] });
    },
  });
}

/**
 * Hook to delete (soft-delete) a suppression rule.
 * Invalidates the suppression rules list on settled.
 */
export function useDeleteSuppressionRule() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (ruleId: number) => deleteSuppressionRule(ruleId),
    onSettled: (_data, error) => {
      if (error) {
        toast.error("Failed to delete suppression rule");
      } else {
        toast.success("Suppression rule deleted");
      }
      queryClient.invalidateQueries({ queryKey: ["suppression-rules"] });
      queryClient.invalidateQueries({ queryKey: ["security-issues"] });
    },
  });
}
